import type { BaseProps, RefundProps } from '../../types/modals'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  BadgeDollarSign,
  Calculator,
  CreditCard,
  Send,
  User,
  Wallet,
} from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  getPaymentConfig,
  postExternalPayment,
  postExternalRefund,
} from '../../services/applicationService'
import { formatAmount } from '@/lib/utils/formatters'
import { showError, showSuccess } from '@/lib/utils/notifications'
import { sendAnalytics } from '@/lib/utils/analytics'
import type { PaymentConfig } from '@/types/payment'
import modalStyles from '../../styles/modals.module.css'
import { Checkbox, Row } from './paymentAdjustmentModalFields'
import {
  EXTERNAL_APPLICATION_FEE_AMOUNT,
  buildExternalPaymentCoResidentList,
  buildExternalPaymentSubmitPayload,
  computeExternalApplicationFeeStartingBalance,
  computeExternalContractStartingBalance,
  computeExternalPaymentCalculatorDisplay,
  computeExternalPaymentFormAmounts,
  parseCalculatorAmount,
  parseSignedCalculatorAmount,
  resolveExternalPaymentAccessFee,
  resolveExternalPaymentBalanceDue,
} from './externalPaymentModalLogic'


const PAYMENT_SOURCE_OPTIONS = [
  { value: 'CARDCONNECT', label: 'Card Connect' },
  { value: 'PAYPAL', label: 'Paypal' },
  { value: 'SMARTBANK', label: 'SmartBank' },
  { value: 'NONE', label: 'None' },
]

const PAYMENT_METHOD_OPTIONS = [
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'ACH', label: 'ACH' },
  { value: 'WIRE-TRANSFER', label: 'Wire-Transfer' },
  { value: 'PAPER_CHECK', label: 'Paper Check' },
]

const paymentTid = 'application-modal-external-payment'
const refundTid = 'application-modal-external-refund'

const CHECK_NUMBER_INPUT_SPINNER_HIDDEN =
  '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'

const CHECK_NUMBER_INPUT_CLASS = `h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 ${CHECK_NUMBER_INPUT_SPINNER_HIDDEN}`

const REFUND_CHECK_NUMBER_INPUT_CLASS = `mt-1 w-full border rounded px-2 py-1 ${CHECK_NUMBER_INPUT_SPINNER_HIDDEN}`

const FIELD_INPUT_CLASS =
  'h-10 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'

const FIELD_LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-600'

const NOTE_MAX_LENGTH = 500

function sanitizeCheckNumberInput(value: string): string {
  return value.replace(/\D/g, '')
}

function blockNonDigitNumberKeys(e: React.KeyboardEvent<HTMLInputElement>) {
  if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
    e.preventDefault()
  }
}


/** External payment modal — Angular PaymentRefundComponent (Add External Payment). */
export function ExternalPaymentModal({
  isOpen,
  onClose,
  applicantId,
  applicationAllData,
  paymentType,
  onSuccess,
}: BaseProps) {
  const isApplicationFee = paymentType === 'APPLICATION_FEE'
  const isContract = !isApplicationFee

  // External-payment header fields
  const [paymentSource, setPaymentSource] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [checkNumber, setCheckNumber] = useState('')
  const [transactionId, setTransactionId] = useState('')
  const [certificatePaidBy, setCertificatePaidBy] = useState('')

  // Calculator + note
  const [adjustmentAmount, setAdjustmentAmount] = useState('')
  const [surchargeFee, setSurchargeFee] = useState('')
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null)
  const [transactionNotes, setTransactionNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [calculatorManuallyEdited, setCalculatorManuallyEdited] = useState(false)
  const [covid19, setCovid19] = useState(false)

  // Reset every open
  useEffect(() => {
    if (!isOpen) return
    setPaymentSource('')
    setPaymentMethod('')
    setCheckNumber('')
    setTransactionId('')
    setCertificatePaidBy('')
    setAdjustmentAmount(
      isApplicationFee ? String(EXTERNAL_APPLICATION_FEE_AMOUNT) : '',
    )
    setSurchargeFee('')
    setTransactionNotes('')
    setCovid19(false)
    setShowConfirm(false)
    setCalculatorManuallyEdited(false)
  }, [isOpen, isApplicationFee])

  // Angular PaymentRefundComponent: load paymentConfig for calculateFee().
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    void (async () => {
      try {
        const cfg = (await getPaymentConfig()) as PaymentConfig
        if (!cancelled) {
          setPaymentConfig(cfg)
        }
      } catch {
        if (!cancelled) setPaymentConfig(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen])

  // Co-resident list — Angular PaymentRefundComponent coresidentLists.
  const coresidentList = useMemo(
    () =>
      buildExternalPaymentCoResidentList(
        applicationAllData,
        paymentType,
        applicantId,
      ),
    [applicationAllData, paymentType, applicantId],
  )

  const paymentHistory = (applicationAllData?.payment_history as
    | Array<Record<string, unknown>>
    | undefined) ?? []

  const startingBalance = useMemo(() => {
    if (!isOpen) return 0
    return isApplicationFee
      ? computeExternalApplicationFeeStartingBalance(paymentHistory, applicantId)
      : computeExternalContractStartingBalance(paymentHistory)
  }, [isOpen, isApplicationFee, paymentHistory, applicantId])

  const accessFee = resolveExternalPaymentAccessFee(applicationAllData)
  const balanceDue = resolveExternalPaymentBalanceDue(applicationAllData)

  const applyExternalPaymentCalculate = useCallback(
    (source: string) => {
      const amounts = computeExternalPaymentFormAmounts({
        isApplicationFee,
        accessFee,
        startingBalance,
        paymentConfig,
        paymentSource: source,
      })
      setAdjustmentAmount(amounts.adjustmentAmount)
      if (isContract) setSurchargeFee(amounts.surchargeAmount)
      setCalculatorManuallyEdited(false)
    },
    [isApplicationFee, isContract, accessFee, startingBalance, paymentConfig],
  )

  const handlePaymentSourceChange = (nextSource: string) => {
    setPaymentSource(nextSource)
    applyExternalPaymentCalculate(nextSource)
  }

  // Re-run Angular paymentCalculate/calculateFee when config loads after source pick.
  useEffect(() => {
    if (!isOpen || !paymentSource) return
    applyExternalPaymentCalculate(paymentSource)
  }, [isOpen, paymentSource, paymentConfig, applyExternalPaymentCalculate])

  // Angular auto-selects sole co-resident for certificate_paid_by.
  useEffect(() => {
    if (!isOpen || !isContract || certificatePaidBy) return
    if (coresidentList.length === 1) {
      setCertificatePaidBy(String(coresidentList[0].fk_user_id))
    }
  }, [isOpen, isContract, certificatePaidBy, coresidentList])

  const surchargeNumeric = parseCalculatorAmount(surchargeFee)

  const adjustmentNumeric = isApplicationFee
    ? EXTERNAL_APPLICATION_FEE_AMOUNT
    : parseSignedCalculatorAmount(adjustmentAmount)

  const calculatorDisplay = computeExternalPaymentCalculatorDisplay({
    isApplicationFee,
    balanceDue,
    startingBalance,
    adjustmentPayment: adjustmentNumeric,
    surchargePayment: surchargeNumeric,
    calculatorManuallyEdited,
  })

  const isPaperCheck = paymentMethod === 'PAPER_CHECK'

  const canSubmit =
    !submitting &&
    !!transactionNotes.trim() &&
    !!adjustmentAmount.trim() &&
    !!paymentSource &&
    !!paymentMethod &&
    (!isPaperCheck || !!checkNumber.trim()) &&
    (!isContract || !!certificatePaidBy)

  const submitConfirmed = async () => {
    if (!canSubmit) {
      showError('Please complete all required fields before submitting.')
      return
    }
    setSubmitting(true)
    setShowConfirm(false)
    sendAnalytics('click', 'external payment paid')
    try {
      await postExternalPayment(
        applicantId,
        buildExternalPaymentSubmitPayload({
          applicantId,
          paymentType,
          adjustmentPayment: adjustmentNumeric,
          surchargePayment: surchargeNumeric,
          paymentSource,
          paymentMethod,
          checkNumber,
          transactionId,
          certificatePaidBy,
          covid19,
          transactionNotes,
          isContract,
          isPaperCheck,
        }),
      )
      showSuccess('Payment Adjustment Added Successfully!')
      onSuccess()
      onClose()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'External payment failed.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSubmitClick = () => {
    if (!canSubmit) {
      showError('Please complete all required fields before submitting.')
      return
    }
    setShowConfirm(true)
  }

  return (
    <>
    <OnScreenModal
      isOpen={isOpen}
      title="Payment Process Adjustment"
      onClose={onClose}
      maxWidth="max-w-3xl"
      headerVariant="iconTitle"
      titleIcon={<BadgeDollarSign className="h-[18px] w-[18px]" />}
      footerAlign="end"
      overlayDataTestId={paymentTid}
      panelDataTestId={`${paymentTid}-panel`}
      titleDataTestId={`${paymentTid}-title`}
      closeButtonDataTestId={`${paymentTid}-close-btn`}
      bodyScrollOnly
      footer={
        <div
          className={`${modalStyles.modalFooterActionGroup} ${modalStyles.modalFooterActionGroupEnd}`}
          data-testid={`${paymentTid}-footer`}
        >
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            data-testid={`${paymentTid}-cancel-btn`}
          >
            Cancel
          </button>
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            disabled={!canSubmit}
            onClick={handleSubmitClick}
            data-testid={`${paymentTid}-submit-button`}
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      }
    >
      <div className="space-y-6 px-1 py-1" data-testid={`${paymentTid}-body`}>
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-slate-900">
            {isApplicationFee ? 'Application Fee' : 'Access Fee'}
          </h3>

          <div
            className="mb-4 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm font-medium text-blue-700"
            data-testid={`${paymentTid}-external-payment-checkbox-label`}
          >
            <div
              className="flex h-4 w-4 items-center justify-center rounded border border-blue-600 bg-blue-600"
              aria-hidden="true"
            >
              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span data-testid={`${paymentTid}-external-payment-checkbox`}>External Payment</span>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={FIELD_LABEL_CLASS} htmlFor={`${paymentTid}-payment-source-input`}>
                Payment Source<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <select
                  id={`${paymentTid}-payment-source-input`}
                  className={FIELD_INPUT_CLASS}
                  value={paymentSource}
                  onChange={(e) => handlePaymentSourceChange(e.target.value)}
                  data-testid={`${paymentTid}-payment-source-input`}
                >
                  <option value="">Select</option>
                  {PAYMENT_SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={FIELD_LABEL_CLASS} htmlFor={`${paymentTid}-payment-method-input`}>
                Payment Method<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <CreditCard
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <select
                  id={`${paymentTid}-payment-method-input`}
                  className={FIELD_INPUT_CLASS}
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  data-testid={`${paymentTid}-payment-method-input`}
                >
                  <option value="">Select</option>
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isPaperCheck ? (
              <div data-testid={`${paymentTid}-check-number-label`}>
                <label className={FIELD_LABEL_CLASS} htmlFor={`${paymentTid}-check-number-input`}>
                  Check Number<span className="text-red-500">*</span>
                </label>
                <input
                  id={`${paymentTid}-check-number-input`}
                  className={CHECK_NUMBER_INPUT_CLASS}
                  type="number"
                  min={0}
                  value={checkNumber}
                  onChange={(e) =>
                    setCheckNumber(sanitizeCheckNumberInput(e.target.value))
                  }
                  onKeyDown={blockNonDigitNumberKeys}
                  data-testid={`${paymentTid}-check-number-input`}
                />
              </div>
            ) : null}

            <div data-testid={`${paymentTid}-transaction-id-label`}>
              <label className={FIELD_LABEL_CLASS} htmlFor={`${paymentTid}-transaction-id-input`}>
                Gateway Transaction
              </label>
              <input
                id={`${paymentTid}-transaction-id-input`}
                className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Enter transaction ID"
                data-testid={`${paymentTid}-transaction-id-input`}
              />
            </div>

            {isContract ? (
              <div data-testid={`${paymentTid}-contract-paid-by-label`}>
                <label className={FIELD_LABEL_CLASS} htmlFor={`${paymentTid}-contract-paid-by-select`}>
                  Contract Paid By<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <select
                    id={`${paymentTid}-contract-paid-by-select`}
                    className={FIELD_INPUT_CLASS}
                    value={certificatePaidBy}
                    onChange={(e) => setCertificatePaidBy(e.target.value)}
                    data-testid={`${paymentTid}-contract-paid-by-select`}
                  >
                    <option value="">Select</option>
                    {coresidentList.map((cr) => (
                      <option key={String(cr.fk_user_id)} value={String(cr.fk_user_id)}>
                        {[cr.first_name, cr.last_name].filter(Boolean).join(' ') ||
                          String(cr.fk_user_id)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-4" data-testid={`${paymentTid}-covid19-label`}>
            <Checkbox
              label="Due to Covid-19"
              checked={covid19}
              onChange={() => setCovid19((value) => !value)}
              testId={`${paymentTid}-covid19`}
            />
          </div>
        </section>

        <section
          className="overflow-hidden rounded-2xl border border-blue-100 bg-blue-50/40"
          data-testid={`${paymentTid}-payment-calculator-table`}
        >
          <div className="flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-5 py-3">
            <Calculator className="h-4 w-4 text-blue-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-900">Payment Calculator</h3>
          </div>

          <div className="grid gap-0 md:grid-cols-[1fr_auto_1fr]">
            <div className="space-y-3 p-5">
              {isApplicationFee ? (
                <Row label="App Fee" value={formatAmount(EXTERNAL_APPLICATION_FEE_AMOUNT)} isBold />
              ) : (
                <Row
                  label="Certificate Fee"
                  value={accessFee ? formatAmount(accessFee) : '$0.00'}
                  isBold
                />
              )}
              <Row label="Starting Balance" value={formatAmount(startingBalance)} isBold />
              <div className="grid grid-cols-[1fr_9rem] items-center gap-4">
                <span className="text-sm font-medium text-slate-500">Adjustment</span>
                {isContract ? (
                  <input
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-right text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    value={adjustmentAmount}
                    onChange={(e) => {
                      setAdjustmentAmount(e.target.value)
                      setCalculatorManuallyEdited(true)
                    }}
                    placeholder="0.00"
                    data-testid={`${paymentTid}-adjustment-amount-input`}
                  />
                ) : (
                  <span className="text-right text-sm font-bold text-slate-900">
                    {formatAmount(EXTERNAL_APPLICATION_FEE_AMOUNT)}
                  </span>
                )}
              </div>
              {isContract ? (
                <div className="grid grid-cols-[1fr_9rem] items-center gap-4">
                  <span className="text-sm font-medium text-slate-500">Surcharge Fee</span>
                  <input
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-right text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    value={surchargeFee}
                    onChange={(e) => {
                      setSurchargeFee(e.target.value)
                      setCalculatorManuallyEdited(true)
                    }}
                    placeholder="0.00"
                    data-testid={`${paymentTid}-surcharge-fee-input`}
                  />
                </div>
              ) : null}
              <Row
                label="Account Balance"
                value={formatAmount(calculatorDisplay.accountBalance)}
                isBold
              />
            </div>

            <div className="hidden border-l border-dashed border-slate-300 md:block" aria-hidden="true" />

            <div className="space-y-3 border-t border-dashed border-slate-300 p-5 md:border-t-0">
              <Row
                label="Refund Amount"
                value={formatAmount(calculatorDisplay.refundAmount)}
                isBold
              />
              <Row
                label="Balance Owed"
                value={formatAmount(calculatorDisplay.balanceOwed)}
                isBold
              />
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-600">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <Wallet className="h-4 w-4" aria-hidden="true" />
                  </span>
                  Net Amount Due
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatAmount(calculatorDisplay.netAmountDue)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div>
          <label
            className="mb-2 block text-sm font-medium text-slate-600"
            htmlFor={`${paymentTid}-note-input`}
          >
            Note<span className="text-blue-500">*</span>
          </label>
          <div className="relative">
            <textarea
              id={`${paymentTid}-note-input`}
              className="h-28 w-full resize-none rounded-xl border border-slate-200 bg-white p-3 pb-8 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              value={transactionNotes}
              onChange={(e) =>
                setTransactionNotes(e.target.value.slice(0, NOTE_MAX_LENGTH))
              }
              placeholder="Enter note here..."
              maxLength={NOTE_MAX_LENGTH}
              data-testid={`${paymentTid}-note-input`}
            />
            <span className="pointer-events-none absolute bottom-3 right-3 text-xs text-slate-400">
              {transactionNotes.length} / {NOTE_MAX_LENGTH}
            </span>
          </div>
        </div>
      </div>
    </OnScreenModal>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Confirm"
        message="Do you want to Process Payment Adjustment?"
        primaryLabel="Yes"
        secondaryLabel="No"
        onPrimary={() => void submitConfirmed()}
        onSecondary={() => setShowConfirm(false)}
        onDismiss={() => setShowConfirm(false)}
        primaryFirst
        presentation="compactAngular"
        overlayDataTestId={`${paymentTid}-process-adjustment-confirm`}
        primaryButtonDataTestId={`${paymentTid}-process-adjustment-confirm-yes-button`}
        secondaryButtonDataTestId={`${paymentTid}-process-adjustment-confirm-no-button`}
        closeButtonDataTestId={`${paymentTid}-process-adjustment-confirm-close-button`}
      />
    </>
  )
}


export function ExternalRefundModal({ isOpen, onClose, applicantId, onSuccess }: RefundProps) {
  const [paymentType, setPaymentType] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentSource, setPaymentSource] = useState('')
  const [transactionNotes, setTransactionNotes] = useState('')
  const [checkNumber, setCheckNumber] = useState('')
  const [paymentKeys, setPaymentKeys] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    void (async () => {
      try {
        const cfg = await getPaymentConfig()
        setPaymentKeys(Object.keys(cfg || {}))
      } catch {
        setPaymentKeys([])
      }
    })()
  }, [isOpen])

  const submit = async () => {
    setSubmitting(true)
    sendAnalytics('click', 'external payment refund')
    try {
      await postExternalRefund(applicantId, {
        payment_type: paymentType,
        payment_amount: paymentAmount,
        payment_source: paymentSource,
        transaction_notes: transactionNotes || undefined,
        check_number: checkNumber || undefined,
      })
      showSuccess('Processed Successfully!')
      onSuccess()
      onClose()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'External refund failed.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="External refund"
      onClose={onClose}
      maxWidth="max-w-md"
      overlayDataTestId={refundTid}
      panelDataTestId={`${refundTid}-panel`}
      titleDataTestId={`${refundTid}-title`}
      closeButtonDataTestId={`${refundTid}-close-btn`}
      bodyScrollOnly
      footer={
        <div className={modalStyles.modalFooterActionGroup} data-testid={`${refundTid}-footer`}>
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            data-testid={`${refundTid}-cancel-btn`}
          >
            Cancel
          </button>
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            disabled={submitting}
            onClick={() => void submit()}
            data-testid={`${refundTid}-submit-button`}
          >
            Submit
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-testid={`${refundTid}-body`}>
        <label className="block text-sm" data-testid={`${refundTid}-payment-type-label`}>
          Payment type
          <input className="mt-1 w-full border rounded px-2 py-1" value={paymentType} onChange={(e) => setPaymentType(e.target.value)} data-testid={`${refundTid}-payment-type-input`} />
        </label>
        <label className="block text-sm" data-testid={`${refundTid}-amount-label`}>
          Amount
          <input className="mt-1 w-full border rounded px-2 py-1" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} data-testid={`${refundTid}-amount-input`} />
        </label>
        <label className="block text-sm" data-testid={`${refundTid}-payment-source-label`}>
          Payment source
          <select
            className="mt-1 w-full border rounded bg-white px-2 py-1"
            value={paymentSource}
            onChange={(e) => setPaymentSource(e.target.value)}
            data-testid={`${refundTid}-payment-source-input`}
          >
            <option value="">Select</option>
            {paymentKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm" data-testid={`${refundTid}-check-number-label`}>
          Check #
          <input
            className={REFUND_CHECK_NUMBER_INPUT_CLASS}
            type="number"
            min={0}
            value={checkNumber}
            onChange={(e) =>
              setCheckNumber(sanitizeCheckNumberInput(e.target.value))
            }
            onKeyDown={blockNonDigitNumberKeys}
            data-testid={`${refundTid}-check-number-input`}
          />
        </label>
        <label className="block text-sm" data-testid={`${refundTid}-notes-label`}>
          Notes
          <textarea className="mt-1 w-full border rounded px-2 py-1" value={transactionNotes} onChange={(e) => setTransactionNotes(e.target.value)} data-testid={`${refundTid}-notes-input`} />
        </label>
      </div>
    </OnScreenModal>
  )
}
